import { useContext, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  BoxFull,
  Button,
  TransactionalSubheader,
  TreeSelection,
} from "iuvity-ui-components";
import { Header } from "../../components";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { AppContext } from "../../context/index";
import { useSchedules } from "../../hooks/useSchedules";
import { SelectButton } from 'primereact/selectbutton';
import { Calendar } from 'primereact/calendar';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { formatScheduleTime } from "../../helpers/formatDate";
import { SCHEDULE_TYPES } from "../../constants";

interface NewSchedulesProps {
  update?: boolean;
}

const formBlankValues = {
  scheduleTypeId: "",
  day: "",
  startTime: "",
  endTime: "",
};

export const NewSchedulesPage: React.FC<NewSchedulesProps> = ({
  update = false,
}) => {
  const navigate = useNavigate();
  const {
    t,
    appState: { user, selectedPointsObject, language },
    setAppState,
  } = useContext(AppContext);

  const { scheduleAction } = useSchedules("ID");
  const { dataScheduleTypes } = useSchedules("TYPES");

  const {
    control,
    getValues,
    setValue,
    formState: { isDirty, isValid },
  } = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: update
        ? {
            scheduleTypeId: selectedPointsObject?.scheduleTypeId,
            day: selectedPointsObject?.day,
            startTime: selectedPointsObject?.scheduleTimes[0]?.start,
            endTime: selectedPointsObject?.scheduleTimes[0]?.end,
            
          }
        : formBlankValues
  });

  const scheduleTypeIdValues = useWatch({
    control: control,
    name: "scheduleTypeId",
  });

  const dayValues = useWatch({
    control: control,
    name: "day",
  });

  const startTimeValues = useWatch({
    control: control,
    name: "startTime",
  });

  const endTimeValues = useWatch({
    control: control,
    name: "endTime",
  });

  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const daysSelectItems = [`${t("PointsOfAttention.dayMONDAY")}`, `${t("PointsOfAttention.dayTUESDAY")}`, `${t("PointsOfAttention.dayWEDNESDAY")}`, `${t("PointsOfAttention.dayTHURSDAY")}`, `${t("PointsOfAttention.dayFRIDAY")}`, `${t("PointsOfAttention.daySATURDAY")}`, `${t("PointsOfAttention.daySUNDAY")}`]
  const [ valueStart, setValueStart] = useState<Date>();
  const [ valueEnd, setValueEnd] = useState<Date>();
  const [isSelected, setIsSelected] = useState<any>({
    EXTENDED: "",
    REGULAR: "",
    HOLIDAY: "",
    INTERMITTENT: "",
  });

  const objTypes: any = {
    EXTENDED: "",
    REGULAR: "",
    HOLIDAY: "",
    INTERMITTENT: "",
}

  const onReturn = () => {
    setAppState((prev) => ({
      ...prev,
      selectedPointsObject: null,
    }));
    navigate(-1);
    window.scrollTo(0, 0);
  };

  const onAction = async () => {
    const formValues = getValues();
    const actionResult = await scheduleAction(update ? "UPDATE" : "CREATE",
    update 
      ? 
        {
          ...formValues,
          id: selectedPointsObject.id,
          startTime: formatScheduleTime(valueStart), 
          endTime: formatScheduleTime(valueEnd),
          statusId: selectedPointsObject?.statusId
        } 
      : 
        {
          ...formValues, 
          statusId: 1, 
          startTime: formatScheduleTime(getValues('startTime')), 
          endTime: formatScheduleTime(getValues('endTime'))
        }
    )
    if (actionResult === "SUCCESS") {
      navigate("/pointsofattention");
      window.scrollTo(0, 0);
    }
  };

  const handleModalProps = (actionType: "action" | "cancel") => {
    switch (actionType) {
      case "action":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t(`PointsOfAttention.${update ? "save" : "create"}ScheduleModalTitle`),
          content: t(`PointsOfAttention.${update ? "save" : "create"}ScheduleModalContent`),
          onConfirm: onAction,
        }));
      case "cancel":
        return setModalProps((prev) => ({
          ...prev,
          visible: true,
          title: t("Users.cancelModalTitle"),
          content: t("Users.cancelModalContent"),
          onConfirm: onReturn,
        }));
      default:
        return;
    }
  };

  const justifyTemplate = (option: any) => {
    return <span className="p-select-card-label">{option}</span>;
  }

  useEffect(() => {
    const hourStart = selectedPointsObject?.scheduleTimes[0]?.start.split(':')[0]
    const minStart = selectedPointsObject?.scheduleTimes[0]?.start.split(':')[1]
    const hourEnd = selectedPointsObject?.scheduleTimes[0]?.end.split(':')[0]
    const minEnd = selectedPointsObject?.scheduleTimes[0]?.end.split(':')[1]
    const dateStart = new Date()
    const dateEnd = new Date()
    dateStart.setHours(+hourStart)
    dateStart.setMinutes(+minStart)
    dateEnd.setHours(+hourEnd)
    dateEnd.setMinutes(+minEnd)
    setValueStart(dateStart);
    setValueEnd(dateEnd);

    if (update) {
      const obj = Object.defineProperty(objTypes, selectedPointsObject?.scheduleType?.name, {value: 'selected'})
      setIsSelected(obj)
    }
  }, [])

  const handleChangeSelectScheduleType = (e: any) => {
    if (!(e?.node?.id === getValues('scheduleTypeId'))) {
        setValue('scheduleTypeId', e?.node?.id)
        const obj = Object.defineProperty(objTypes, language === 'en' ? e.node.label.toUpperCase() : SCHEDULE_TYPES[e.node.label], {value: 'selected'})
        setIsSelected(obj)
      } else {
        setIsSelected(objTypes)
        setValue('scheduleTypeId', '')
    }
  }

  const handleTimes = (value: any) => {
    setValue('startTime', value);
    setValue('endTime', '');
  }

  const formatHour = (data: any) => {
    if(typeof data === 'string') {
      let date = new Date('1970-01-01T' + data);
      return date?.toLocaleTimeString('en-EN', { hour: '2-digit', minute: '2-digit', hour12: true });
    }
    return data?.toLocaleTimeString('en-EN', { hour: '2-digit', minute: '2-digit', hour12: true });
  }

  const startTimeUpdate = formatHour(selectedPointsObject?.scheduleTimes[0]?.start);
  const endTimeUpdate = formatHour(selectedPointsObject?.scheduleTimes[0]?.end);

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}

      <TransactionalSubheader
        subheaderTitle={
          <div style={{ position: "relative", left: "-40px" }}>
            <i
              className="iu iu-icon-left pr-3"
              onClick={onReturn}
              aria-hidden="true"
              style={{
                fontSize: "var(--font-size-tablet-title)",
                cursor: "pointer",
              }}
            />
            {update ? t("PointsOfAttention.editScheduleTitle") : t("PointsOfAttention.createScheduleTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={t("MappedFields.mappedFieldsTitle")}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("PointsOfAttention.pointsTitle"),
              command: () => onReturn(),
            },
            {
              label: t(`PointsOfAttention.${update ? 'edit' : 'create'}ScheduleTitle`),
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
      </TransactionalSubheader>

      <BoxFull
        className="flex px-0"
        variant="tertiary"
      >
        <p>{update ? t("PointsOfAttention.editScheduleDescription") : t("PointsOfAttention.createScheduleDescription")}</p>
      </BoxFull>
      <div className={`container-treeboxes mt-5`} id='PreparationFormContainer'>
        <div className={`treeboxes-child general-form start`}>
          <div className="treeboxes-title">
            <h2>{t('PointsOfAttention.typeOfScheduleLabel')}</h2>
          </div>
          <div className="treeboxes-item treeboxes-item__gray" style={{ height: '80%' }} id='SelectScheduleContainer'>
            <p className="points-subtitle">{update ? t("PointsOfAttention.editTypeOfScheduleSubtitle") : t("PointsOfAttention.typeOfScheduleSubtitle")}</p>
              <TreeSelection
                id='TransferTypesContainer'
                children={<></>}
                value={dataScheduleTypes?.map((item: any) => {
                  return {
                      id: item.id,
                      label: item.name,
                      // @ts-ignore
                      className: `schedule-card-${isSelected[language === 'en' ? item.name.toUpperCase() : SCHEDULE_TYPES[item.name]]}`,
                  }
                })}
                selectionMode='single'
                selectionKeys={getValues('scheduleTypeId')}
                onSelect={(e: any) => handleChangeSelectScheduleType(e)}
                style={{ margin: '0 auto'}}
                className='col-8'
              />
          </div>
        </div>
        <div className={`treeboxes-child general-form mid`}>
          <div className="treeboxes-title">
            <h2>{t('PointsOfAttention.dayLabel')}</h2>
          </div>
          <div className="treeboxes-item treeboxes-item__white" style={{ height: '80%' }} id='SelectScheduleContainer'>
            <p className="points-subtitle">{update ? t("PointsOfAttention.editDaySubtitle") : t("PointsOfAttention.createDaySubtitle")}</p>
            <PerfectScrollbar>
              <SelectButton
                value={getValues('day')} 
                options={daysSelectItems} 
                onChange={(e) => setValue('day', e.value)}
                itemTemplate={justifyTemplate}
              />
            </PerfectScrollbar>
          </div>
        </div>
        <div className={`treeboxes-child general-form end`}>
          <div className="treeboxes-title">
            <h2>{t('PointsOfAttention.hourLabel')}</h2>
          </div>
          <div className="treeboxes-item treeboxes-item__gray" style={{ height: '80%' }} id='SelectScheduleContainer'>
            <p className="points-subtitle">{update ? t("PointsOfAttention.editHourSubtitle") : t("PointsOfAttention.createHourSubtitle")}</p>
            <div className="box-hours">
              <p>{t("PointsOfAttention.startHourLabel")}</p>
              <Calendar
                id="time12"
                value={update ? valueStart : getValues('startTime')}
                onChange={(e: any) => handleTimes(e.value)}
                timeOnly
                hourFormat="12"
                maxDate={update ? valueEnd : getValues('endTime')}
              />
              <p>{t("PointsOfAttention.finalHourLabel")}</p>
              <Calendar
                id="time12"
                value={update ? valueEnd : getValues('endTime')}
                onChange={(e: any) => setValue('endTime', e.value)}
                timeOnly
                hourFormat="12"
                minDate={update ? valueStart : getValues('startTime')}
              />
            </div>
          </div>
        </div>
      </div>

      <BoxFull className="my-5 p-0" style={{ backgroundColor: "transparent" }}>
        <div className="flex align-items-center justify-content-end gap-5">
          <Button
            variant="p-button-link"
            label={t("PointsOfAttention.cancelBtnLabel")}
            onClick={
                (!scheduleTypeIdValues || selectedPointsObject?.scheduleTypeId === getValues('scheduleTypeId')) &&
                (!dayValues || selectedPointsObject?.day === getValues('day')) &&
                (!startTimeUpdate || startTimeUpdate === formatHour(startTimeValues)) &&
                (!endTimeUpdate || endTimeUpdate === formatHour(endTimeValues) || !endTimeValues) &&
                !isDirty
                ? onReturn 
                : () => handleModalProps("cancel")
            }
          />
          <Button
            variant="p-button"
            label={
              update
                ? t("PointsOfAttention.saveBtnLabel")
                : t("PointsOfAttention.createBtnLabel")
            }
            disabled={ update
              ?
                (!scheduleTypeIdValues || selectedPointsObject?.scheduleTypeId === getValues('scheduleTypeId')) &&
                (!dayValues || selectedPointsObject?.day === getValues('day')) &&
                (!startTimeUpdate || startTimeUpdate === formatHour(startTimeValues)) &&
                (!endTimeUpdate || endTimeUpdate === formatHour(endTimeValues) || !endTimeValues) &&
                !isDirty
              :
                !(
                  scheduleTypeIdValues &&
                  dayValues?.length > 0 &&
                  startTimeValues > 0 &&
                  endTimeValues > 0 &&
                  isValid
                )
            }
            onClick={() => handleModalProps("action")}
          />
        </div>
      </BoxFull>

      <CustomDialog
        title={modalProps.title}
        visible={modalProps.visible}
        content={modalProps.content}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
